import styles from './Sidebar.scss';
import PropTypes from 'prop-types';
import { Component } from 'react';
import lodash from 'lodash';
import { Link } from 'react-router';
import { locationShape } from 'react-router';

import {
    UserCanSeeInternalUsers,
    UserCanSeeExternalUsers,
    UserCanSeeBinderDetails,
    UserCanSeeBinderSetup,
    UserCanSeeBinderTimeline,
    UserCanCompileBinder,
    UserCanSeeUnfiledDocuments
} from 'common/permissions/components/sidebar';

export class SidebarLink extends Component {
    constructor(props) {
        super(props);
        this.state = {
            expanded: props.link.expanded
        };
        this.handleClick = this.handleClick.bind(this);
    }

    handleClick() {
        const { expanded } = this.state;
        this.setState({ expanded: !expanded });
    }

    shouldShowRootLinkAsActive() {
        const { isRoot } = this.props.link;
        const { folder } = this.context.location.query;
        return isRoot && lodash.isNil(folder);
    }

    render() {
        const { link, iconClass } = this.props;

        const { label, action, fileCount } = link;
        let children = this.renderChildren();

        const className = this.shouldShowRootLinkAsActive() ? styles.activeLink : styles.link;

        return (
            <div className={styles.linkWrapper} data-cy="Sidebar">
                <div className={styles.linkRow}>
                    <SidebarLinkIcon link={link} iconClass={iconClass} />
                    <Link
                        to={action}
                        className={className}
                        activeClassName={styles.activeLink}
                        onlyActiveOnIndex={true}>
                        {label}
                    </Link>
                    <FileCountDisplay fileCount={fileCount} />
                    <FolderExpandArrow
                        action={action}
                        expanded={this.state.expanded}
                        link={this.props.link}
                        location={this.context.location}
                        handleClick={this.handleClick} />
                </div>
                {children}
            </div>
        );
    }

    renderChildren() {
        const { expanded } = this.state;
        const { link } = this.props;
        const isShowingChildren = expanded && !lodash.isNil(link.children) && Array.isArray(link.children) && link.children.length > 0;
        if (isShowingChildren) {
            return (
                <div className={styles.subLinks}>
                    {link.children.map(l => <SidebarLink key={l.id} link={l}/>)}
                </div>
            );
        }
        return null;
    }
}

const SidebarLinkIcon = ({ link, iconClass }) => {
    const isSidebarIconVisible = !lodash.isNil(iconClass) &&
        !lodash.isNil(link.children) &&
        Array.isArray(link.children) &&
        link.children.length !== 0;
    if (isSidebarIconVisible) {
        return <span className={iconClass}/>;
    }
    return null;
};

const FileCountDisplay = ({fileCount}) => {
    const shouldDisplayFileCount = lodash.isFinite(fileCount);
    if (shouldDisplayFileCount) {
        return (
            <span className={styles.fileCountDisplay}>
                {fileCount}
            </span>
        );
    }
    return null;
};

const FolderExpandArrow = ({ expanded, link, location, action, handleClick }) => {
    if (lodash.isNil(link.children) || !Array.isArray(link.children) || link.children.length === 0) {
        return null;
    }
    const iconName = expanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right';
    const className = lodash.isEqual(location.query, action.query) ? styles.activeArrow : styles.arrow;
    return (
        <span className={className} onClick={handleClick}>
            {iconName}
        </span>
    );
};

SidebarLink.contextTypes = {
    location: locationShape.isRequired
};

class SidebarLinks extends Component {

    chooseSidebarLinkClass(link) {
        switch (link.kind) {
            case 'details':
                return UserCanSeeBinderDetails(SidebarLink);
            case 'internalUsers':
                return UserCanSeeInternalUsers(SidebarLink);
            case 'externalUsers':
                return UserCanSeeExternalUsers(SidebarLink);
            case 'editBinderDetails':
                return UserCanSeeBinderSetup(SidebarLink);
            case 'timeline':
                return UserCanSeeBinderTimeline(SidebarLink);
            case 'compile':
                return UserCanCompileBinder(SidebarLink);
            case 'unfiledDocuments':
                return UserCanSeeUnfiledDocuments(SidebarLink);
            default:
                return SidebarLink;
        }
    }

    render() {
        const { links, iconClass } = this.props;

        const renderedSidebarLinks = links.map((l, i) => {
            const SidebarLinkClass = this.chooseSidebarLinkClass(l);
            return <SidebarLinkClass key={i} link={l} iconClass={iconClass}/>;
        });

        return (
            <section className={styles.links} data-cy="renderedSidebarLinks">
                {renderedSidebarLinks}
            </section>
        );
    }
}

export class Sidebar extends Component {
    constructor(props) {
        super(props);
        this.state = {
            links: props.links || []
        };
    }

    getSideBarStyle() {
        const { fromStudy, fixedNav } = this.props;
        if(fromStudy && fixedNav) {
            return styles.stuckSidebarStudy;
        }
        else if(fixedNav) {
            return styles.stuckSidebarBinder;
        }
        return styles.sidebar;
    }

    render() {
        const { links, iconClass, title, isCollapsed } = this.props;
        const sidebarStyle = this.getSideBarStyle();
        if (isCollapsed) {
            return null;
        }

        if (lodash.isNil(title)) {
            return null;
        }
        return (
            <div className={sidebarStyle} data-cy="getSideBarStyle">
                <SidebarLinks links={links} iconClass={iconClass}/>
            </div>
        );
    }
}

Sidebar.propTypes = {
    links: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        action: PropTypes.oneOfType([
            PropTypes.string,
            locationShape
        ]).isRequired,
        tooltip: PropTypes.string
    }))
};

export default Sidebar;
